import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Copy,
  Check,
  Heart,
  Share2,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdBlock from "@/components/AdBlock";
import api from "@/lib/api";

const CATEGORY_LABEL_STYLES = {
  Cyberpunk: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/25",
  Corporate: "text-white/85 bg-white/5 border-white/15",
  Editorial: "text-white/85 bg-white/5 border-white/15",
  Anime: "text-[#E0007A] bg-[#E0007A]/10 border-[#E0007A]/30",
  Vintage: "text-amber-200 bg-amber-200/10 border-amber-200/25",
  Minimalist: "text-white/80 bg-white/5 border-white/15",
  Street: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/25",
  Fantasy: "text-violet-300 bg-violet-300/10 border-violet-300/25",
  Cottagecore: "text-amber-100 bg-amber-100/10 border-amber-100/25",
};

function parseParameters(input) {
  if (!input || typeof input !== "string") return [];
  const raw = input.trim();
  if (!raw) return [];
  if (raw.startsWith("{")) {
    try {
      const obj = JSON.parse(raw);
      return Object.entries(obj).map(([k, v]) => ({ key: k, value: String(v) }));
    } catch {}
  }
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const idx = p.indexOf(":");
      if (idx === -1) return { key: "Tag", value: p };
      return { key: p.slice(0, idx).trim(), value: p.slice(idx + 1).trim() };
    })
    .filter((x) => x.value);
}

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
}

export default function PromptDetail() {
  const { id } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/prompts/${id}`)
      .then(({ data }) => {
        if (!mounted) return;
        setPrompt(data);
        setLikes(data.likes_count || 0);
        document.title = `${data.title} — AI Profile Prompt · Giprompthub`;
        const desc = (data.explanation_text || "").slice(0, 155).trim();
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
          m = document.createElement("meta");
          m.setAttribute("name", "description");
          document.head.appendChild(m);
        }
        m.setAttribute(
          "content",
          desc + (data.explanation_text && data.explanation_text.length > 155 ? "…" : ""),
        );
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  const writeToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  };

  const handleCopy = async () => {
    if (!prompt) return;
    await writeToClipboard(prompt.prompt_text);
    setCopied(true);
    toast.success("Prompt Copied! Paste into Gemini", {
      description: prompt.title,
      duration: 2500,
    });
    setTimeout(() => setCopied(false), 1800);
  };

  const handleOpenGemini = async () => {
    if (!prompt) return;
    await writeToClipboard(prompt.prompt_text);
    toast.success("Prompt copied! Redirecting to Gemini…", { duration: 2200 });
    window.open("https://gemini.google.com/", "_blank", "noopener,noreferrer");
  };

  const handleLike = async () => {
    if (!prompt || liked) return;
    setLiked(true);
    setLikes((n) => n + 1);
    try {
      const { data } = await api.post(`/prompts/${prompt.id}/like`);
      setLikes(data.likes_count);
    } catch {
      setLiked(false);
      setLikes((n) => Math.max(0, n - 1));
    }
  };

  const openShare = (platform) => {
    if (!prompt) return;
    const shareUrl = `${window.location.origin}/prompt/${prompt.id}`;
    const shareText = `${prompt.title} — free AI profile prompt`;
    const enc = encodeURIComponent;
    let url = "";
    if (platform === "twitter") {
      url = `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`;
    } else if (platform === "pinterest") {
      url = `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(
        resolveUrl(prompt.image_url),
      )}&description=${enc(shareText)}`;
    } else if (platform === "whatsapp") {
      const msg = `Check out this awesome AI profile style! Copy the exact prompt for free here: ${shareUrl}`;
      url = `https://wa.me/?text=${enc(msg)}`;
    } else if (platform === "copy") {
      writeToClipboard(shareUrl);
      toast("Share link copied");
      return;
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer,width=640,height=580");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <main className="max-w-[1100px] mx-auto px-5 md:px-10 py-8 md:py-12">
        <Link
          to="/"
          data-testid="back-to-gallery"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors font-mono-label"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to gallery
        </Link>

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        )}

        {notFound && !loading && (
          <div className="py-24 text-center" data-testid="prompt-not-found">
            <div className="font-display text-3xl mb-2">Prompt not found</div>
            <p className="text-white/50">
              This prompt may have been removed. Browse the full library instead.
            </p>
          </div>
        )}

        {prompt && (
          <article
            data-testid={`prompt-detail-${prompt.id}`}
            className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 lg:gap-12"
          >
            <div>
              {/* Category label above image */}
              <div className="mb-4">
                <span
                  data-testid="detail-category-label"
                  className={`inline-flex items-center text-sm md:text-base font-extrabold uppercase px-3 py-1.5 rounded-md border ${
                    CATEGORY_LABEL_STYLES[prompt.style_category] ||
                    "text-white/85 bg-white/5 border-white/15"
                  }`}
                  style={{ letterSpacing: "0.06em" }}
                >
                  {prompt.style_category}
                </span>
              </div>

              {/* Hero image */}
              {prompt.image_url && (
                <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0E0E12] aspect-portrait max-h-[640px]">
                  <img
                    src={resolveUrl(prompt.image_url)}
                    alt={prompt.title}
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* H1 title */}
              <h1
                data-testid="detail-title"
                className="mt-6 font-display text-3xl md:text-5xl tracking-tight leading-[1.05]"
              >
                {prompt.title}
              </h1>

              {/* Full explanation — no truncation, no collapse. Semantic h2 for SEO. */}
              <section className="mt-8" data-testid="detail-explanation-section">
                <h2 className="font-display text-lg md:text-xl tracking-tight text-white/50 uppercase mb-3">
                  Style breakdown
                </h2>
                <p
                  data-testid="detail-explanation-text"
                  className="text-base md:text-lg text-white/80 leading-relaxed whitespace-pre-line"
                >
                  {prompt.explanation_text}
                </p>
              </section>

              {/* Full prompt text */}
              <section className="mt-10" data-testid="detail-prompt-section">
                <h2 className="font-display text-lg md:text-xl tracking-tight text-white/50 uppercase mb-3">
                  The prompt
                </h2>
                <div className="rounded-xl border border-white/10 bg-black/40 p-5">
                  <p
                    data-testid="detail-prompt-text"
                    className="text-sm md:text-base text-white/90 font-mono leading-relaxed whitespace-pre-wrap break-words"
                  >
                    {prompt.prompt_text}
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={handleCopy}
                    data-testid={`copy-prompt-btn-${prompt.id}`}
                    className={`inline-flex items-center gap-2 rounded-full py-3 px-6 font-semibold text-sm transition-all ${
                      copied
                        ? "bg-white text-[#050505]"
                        : "bg-[#E0007A] hover:bg-[#ff1493] text-white glow-magenta hover:scale-[1.02]"
                    }`}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? "Copied — paste into Gemini" : "Click to Copy Prompt"}
                  </button>
                  <button
                    onClick={handleOpenGemini}
                    data-testid={`open-gemini-btn-${prompt.id}`}
                    className="inline-flex items-center gap-2 rounded-full py-3 px-6 font-medium text-sm border border-white/15 hover:border-[#00E5FF]/60 text-white/80 hover:text-[#00E5FF] transition-colors"
                  >
                    Open in Gemini
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </button>
                </div>
              </section>

              {/* How to use — always rendered when present */}
              {prompt.how_to_use && (
                <section
                  className="mt-10"
                  data-testid={`prompt-how-to-use-${prompt.id}`}
                >
                  <h2 className="font-display text-lg md:text-xl tracking-tight text-[#00E5FF] uppercase mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    How to use
                  </h2>
                  <div className="rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/[0.03] p-5">
                    <p className="text-base text-white/85 leading-relaxed whitespace-pre-line">
                      {prompt.how_to_use}
                    </p>
                  </div>
                </section>
              )}

              {/* Parameters */}
              {parseParameters(prompt.parameters).length > 0 && (
                <section
                  className="mt-10"
                  data-testid={`prompt-parameters-${prompt.id}`}
                >
                  <h2 className="font-display text-lg md:text-xl tracking-tight text-white/50 uppercase mb-3">
                    Prompt parameters
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {parseParameters(prompt.parameters).map((p, i) => (
                      <span
                        key={`${p.key}-${i}`}
                        data-testid={`prompt-param-badge-${prompt.id}-${i}`}
                        className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm"
                      >
                        <span className="text-white/50 font-mono uppercase tracking-wide text-[11px]">
                          {p.key}
                        </span>
                        <span className="text-white font-medium">{p.value}</span>
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Like + share row */}
              <section className="mt-10 pt-6 border-t border-white/5 flex items-center justify-between flex-wrap gap-4">
                <button
                  onClick={handleLike}
                  data-testid={`like-btn-${prompt.id}`}
                  className="inline-flex items-center gap-2 text-white/70 hover:text-[#E0007A] transition-colors"
                >
                  <Heart className={`h-5 w-5 ${liked ? "fill-[#E0007A] text-[#E0007A]" : ""}`} />
                  <span className="font-mono text-sm">{likes} likes</span>
                </button>
                <div className="flex items-center gap-2">
                  <span className="font-mono-label text-white/40 mr-2">Share</span>
                  <button
                    onClick={() => openShare("twitter")}
                    data-testid={`share-twitter-${prompt.id}`}
                    className="h-9 w-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                    aria-label="Share on X"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                      <path d="M18.244 2H21.5l-7.53 8.61L22.5 22h-6.79l-5.32-6.95L4.28 22H1l8.06-9.22L1.5 2h6.94l4.81 6.36L18.244 2Z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openShare("pinterest")}
                    data-testid={`share-pinterest-${prompt.id}`}
                    className="h-9 w-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                    aria-label="Share on Pinterest"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                      <path d="M12.04 2C6.51 2 2 6.51 2 12.04c0 4.23 2.62 7.85 6.32 9.31-.09-.79-.17-2 .04-2.86.19-.78 1.23-4.95 1.23-4.95s-.31-.63-.31-1.55c0-1.45.84-2.53 1.88-2.53.89 0 1.32.67 1.32 1.47 0 .9-.57 2.24-.87 3.48-.25 1.04.52 1.89 1.55 1.89 1.87 0 3.3-1.97 3.3-4.82 0-2.52-1.81-4.28-4.4-4.28-3 0-4.76 2.25-4.76 4.57 0 .91.35 1.88.79 2.41.09.1.1.19.07.29-.08.32-.25 1.04-.29 1.19-.05.19-.15.24-.35.15-1.32-.61-2.14-2.53-2.14-4.08 0-3.32 2.41-6.37 6.96-6.37 3.65 0 6.49 2.6 6.49 6.08 0 3.63-2.29 6.55-5.47 6.55-1.07 0-2.07-.55-2.41-1.21l-.66 2.5c-.24.92-.88 2.08-1.31 2.78.99.3 2.03.47 3.13.47 5.53 0 10.04-4.51 10.04-10.04C22.08 6.51 17.57 2 12.04 2Z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openShare("whatsapp")}
                    data-testid={`share-whatsapp-${prompt.id}`}
                    className="h-9 w-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                    aria-label="Share on WhatsApp"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.02 0C5.4 0 .04 5.36.04 11.98c0 2.11.55 4.17 1.6 5.99L0 24l6.2-1.62a11.98 11.98 0 0 0 5.82 1.48h.01c6.62 0 11.98-5.36 11.98-11.98 0-3.2-1.25-6.21-3.49-8.4Zm-8.5 18.4h-.01a9.94 9.94 0 0 1-5.06-1.38l-.36-.21-3.68.96.98-3.59-.24-.37a9.9 9.9 0 0 1-1.52-5.31c0-5.49 4.47-9.96 9.97-9.96 2.66 0 5.16 1.04 7.04 2.92a9.9 9.9 0 0 1 2.92 7.05c-.01 5.49-4.48 9.96-9.98 9.96Zm5.47-7.46c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15s-.78.97-.96 1.17c-.18.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.79-1.67-2.09-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.68-1.63-.93-2.24-.24-.58-.5-.5-.68-.5-.18 0-.38-.03-.58-.03s-.53.08-.8.38c-.28.3-1.05 1.03-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.13 3.25 5.16 4.56.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.18-1.42-.07-.13-.28-.2-.58-.35Z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openShare("copy")}
                    data-testid={`share-copy-link-${prompt.id}`}
                    className="h-9 w-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                    aria-label="Copy share link"
                  >
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </section>

              <div className="mt-10">
                <AdBlock variant="inline" testId="ad-detail-inline" />
              </div>
            </div>

            <aside className="space-y-6">
              <div className="lg:sticky lg:top-24">
                <AdBlock variant="sidebar" testId="ad-detail-sidebar" />
              </div>
            </aside>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
}
